/**
 * Enhanced Error Handling System
 * Provides comprehensive error management with retry logic, offline detection,
 * and user-friendly feedback throughout the application
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import logger from '../utils/logger';

// Error types for categorization
export const ErrorTypes = {
  NETWORK: 'network',
  AUTH: 'auth',
  VALIDATION: 'validation',
  SERVER: 'server',
  AI: 'ai',
  TIMEOUT: 'timeout',
  RATE_LIMIT: 'rate_limit',
  NOT_FOUND: 'not_found',
  FORBIDDEN: 'forbidden',
  UNKNOWN: 'unknown'
};

// Error severity levels
export const ErrorSeverity = {
  LOW: 'low',
  MEDIUM: 'medium',
  HIGH: 'high',
  CRITICAL: 'critical'
};

// User-friendly error messages by type
const ERROR_MESSAGES = {
  [ErrorTypes.NETWORK]: {
    title: 'Connection Error',
    message: 'Unable to connect to the server. Please check your internet connection.',
    action: 'Check your connection and try again.',
    retryable: true
  },
  [ErrorTypes.AUTH]: {
    title: 'Authentication Required',
    message: 'Your session has expired or you need to log in.',
    action: 'Please log in again to continue.',
    retryable: false
  },
  [ErrorTypes.VALIDATION]: {
    title: 'Invalid Input',
    message: 'Please check your input and correct any errors.',
    action: 'Review the highlighted fields and try again.',
    retryable: false
  },
  [ErrorTypes.SERVER]: {
    title: 'Server Error',
    message: 'Something went wrong on our end.',
    action: 'Please try again in a few moments.',
    retryable: true
  },
  [ErrorTypes.AI]: {
    title: 'AI Service Unavailable',
    message: 'The AI service is temporarily busy or unavailable.',
    action: 'Please wait a moment and try again.',
    retryable: true
  },
  [ErrorTypes.TIMEOUT]: {
    title: 'Request Timeout',
    message: 'The request took too long to complete.',
    action: 'Please try again. If the problem persists, try a simpler request.',
    retryable: true
  },
  [ErrorTypes.RATE_LIMIT]: {
    title: 'Too Many Requests',
    message: 'You\'ve made too many requests in a short time.',
    action: 'Please wait a minute before trying again.',
    retryable: true,
    retryDelay: 60000
  },
  [ErrorTypes.NOT_FOUND]: {
    title: 'Not Found',
    message: 'The requested resource could not be found.',
    action: 'The item may have been moved or deleted.',
    retryable: false
  },
  [ErrorTypes.FORBIDDEN]: {
    title: 'Access Denied',
    message: 'You don\'t have permission to perform this action.',
    action: 'Contact your administrator if you need access.',
    retryable: false
  },
  [ErrorTypes.UNKNOWN]: {
    title: 'Unexpected Error',
    message: 'An unexpected error occurred.',
    action: 'Please try again. If the problem continues, contact support.',
    retryable: true
  }
};

/**
 * Classify error type from error object or response
 */
export function classifyError(error, response = null) {
  // Check response status first
  if (response) {
    if (response.status === 401) return ErrorTypes.AUTH;
    if (response.status === 403) return ErrorTypes.FORBIDDEN;
    if (response.status === 404) return ErrorTypes.NOT_FOUND;
    if (response.status === 422) return ErrorTypes.VALIDATION;
    if (response.status === 429) return ErrorTypes.RATE_LIMIT;
    if (response.status >= 500) return ErrorTypes.SERVER;
  }

  // Check error message patterns
  const errorMessage = error?.message?.toLowerCase() || error?.toString()?.toLowerCase() || '';
  
  if (errorMessage.includes('failed to fetch') || 
      errorMessage.includes('network') || 
      errorMessage.includes('offline') ||
      errorMessage.includes('connection')) {
    return ErrorTypes.NETWORK;
  }
  
  if (errorMessage.includes('timeout') || errorMessage.includes('timed out')) {
    return ErrorTypes.TIMEOUT;
  }
  
  if (errorMessage.includes('unauthorized') || 
      errorMessage.includes('token') || 
      errorMessage.includes('session expired')) {
    return ErrorTypes.AUTH;
  }
  
  if (errorMessage.includes('validation') || 
      errorMessage.includes('invalid') || 
      errorMessage.includes('required field')) {
    return ErrorTypes.VALIDATION;
  }
  
  if (errorMessage.includes('ai service') || 
      errorMessage.includes('acumen') ||
      errorMessage.includes('claude') || 
      errorMessage.includes('anthropic') ||
      errorMessage.includes('openai')) {
    return ErrorTypes.AI;
  }
  
  if (errorMessage.includes('rate limit') || errorMessage.includes('too many')) {
    return ErrorTypes.RATE_LIMIT;
  }
  
  return ErrorTypes.UNKNOWN;
}

/**
 * Get user-friendly error info
 */
export function getErrorInfo(error, response = null) {
  const type = classifyError(error, response);
  const baseInfo = ERROR_MESSAGES[type] || ERROR_MESSAGES[ErrorTypes.UNKNOWN];
  
  // Extract specific error details if available
  let specificMessage = null;
  if (error?.detail) {
    specificMessage = error.detail;
  } else if (error?.message && !error.message.includes('Failed to fetch')) {
    specificMessage = error.message;
  }
  
  return {
    type,
    ...baseInfo,
    specificMessage,
    originalError: error
  };
}

/**
 * Enhanced retry logic with exponential backoff
 */
export async function withRetry(
  fn,
  options = {}
) {
  const {
    maxRetries = 3,
    baseDelay = 1000,
    maxDelay = 10000,
    onRetry = null,
    shouldRetry = (error) => {
      const type = classifyError(error);
      return ERROR_MESSAGES[type]?.retryable !== false;
    }
  } = options;

  let lastError;
  
  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      
      if (attempt === maxRetries || !shouldRetry(error)) {
        throw error;
      }
      
      const delay = Math.min(baseDelay * Math.pow(2, attempt), maxDelay);
      
      if (onRetry) {
        onRetry(attempt + 1, maxRetries, delay, error);
      }
      
      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
  
  throw lastError;
}

/**
 * Error Context for global error state management
 */
const ErrorContext = createContext(null);

export function ErrorProvider({ children }) {
  const [errors, setErrors] = useState([]);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  
  // Monitor online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);
  
  const addError = useCallback((error, context = '') => {
    const errorInfo = getErrorInfo(error);
    const id = Date.now() + Math.random();
    
    setErrors(prev => [...prev, {
      id,
      ...errorInfo,
      context,
      timestamp: new Date()
    }]);
    
    // Auto-remove non-critical errors after 10 seconds
    if (errorInfo.type !== ErrorTypes.AUTH && errorInfo.type !== ErrorTypes.NETWORK) {
      setTimeout(() => {
        setErrors(prev => prev.filter(e => e.id !== id));
      }, 10000);
    }
    
    return id;
  }, []);
  
  const removeError = useCallback((id) => {
    setErrors(prev => prev.filter(e => e.id !== id));
  }, []);
  
  const clearErrors = useCallback(() => {
    setErrors([]);
  }, []);
  
  return (
    <ErrorContext.Provider value={{
      errors,
      isOffline,
      addError,
      removeError,
      clearErrors
    }}>
      {children}
    </ErrorContext.Provider>
  );
}

export function useError() {
  const context = useContext(ErrorContext);
  if (!context) {
    throw new Error('useError must be used within an ErrorProvider');
  }
  return context;
}

/**
 * Error Boundary Component for catching React errors
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    logger.error('React Error Boundary caught an error:', error, errorInfo);
    
    // Log to error tracking service if available
    if (window.Sentry) {
      window.Sentry.captureException(error, { extra: errorInfo });
    }
  }

  render() {
    if (this.state.hasError) {
      const { fallback: Fallback, onReset } = this.props;
      
      if (Fallback) {
        return (
          <Fallback 
            error={this.state.error}
            errorInfo={this.state.errorInfo}
            onReset={() => {
              this.setState({ hasError: false, error: null, errorInfo: null });
              onReset?.();
            }}
          />
        );
      }
      
      return (
        <div className="min-h-[200px] flex items-center justify-center p-6">
          <div className="text-center max-w-md">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
              <svg className="w-8 h-8 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
              Something went wrong
            </h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              An unexpected error occurred. Please try refreshing the page.
            </p>
            <button
              onClick={() => {
                this.setState({ hasError: false, error: null, errorInfo: null });
                onReset?.();
              }}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Try Again
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

/**
 * Offline Banner Component
 */
export function OfflineBanner() {
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [wasOffline, setWasOffline] = useState(false);
  
  useEffect(() => {
    const handleOnline = () => {
      setIsOffline(false);
      if (wasOffline) {
        // Show "back online" message briefly
        setTimeout(() => setWasOffline(false), 3000);
      }
    };
    const handleOffline = () => {
      setIsOffline(true);
      setWasOffline(true);
    };
    
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [wasOffline]);
  
  if (!isOffline && !wasOffline) return null;
  
  return (
    <div className={`fixed top-0 left-0 right-0 z-50 px-4 py-2 text-center text-sm font-medium transition-colors ${
      isOffline 
        ? 'bg-red-600 text-white' 
        : 'bg-green-600 text-white'
    }`}>
      {isOffline ? (
        <span className="flex items-center justify-center gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3m8.293 8.293l1.414 1.414" />
          </svg>
          You're offline. Some features may not work.
        </span>
      ) : (
        <span className="flex items-center justify-center gap-2">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
          You're back online!
        </span>
      )}
    </div>
  );
}

export default {
  ErrorTypes,
  ErrorSeverity,
  classifyError,
  getErrorInfo,
  withRetry,
  ErrorProvider,
  useError,
  ErrorBoundary,
  OfflineBanner
};
